// Slide-in cart. Progressive enhancement over plain Shopify forms:
// - product forms (action …/cart/add) are sent with fetch, then the drawer opens with fresh contents
//   (button says "Added ✓", the Fine Goods seal is pressed onto the header bag icon with the new count)
// - the header "Cart (n)" links open the drawer instead of the cart page
// - −/+/remove in the drawer call /cart/change.js
// Drawer HTML comes from the Section Rendering API (sections/fg-cart-drawer.liquid), so Liquid stays the
// single source of truth. Any failure falls back to the normal page flow.
(() => {
  const dialog = document.querySelector('[data-fg-drawer]');
  if (!dialog || typeof dialog.showModal !== 'function' || !window.fetch) return;

  const SECTION = 'fg-cart-drawer';
  const routes = window.routes || {};
  const onCartPage = document.querySelector('main[data-template]')?.dataset.template === 'cart';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let opener = null;

  const body = () => dialog.querySelector('[data-fg-drawer-body]');

  const render = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-fg-drawer-body]');
    if (!fresh) return;
    body().replaceChildren(...fresh.childNodes);

    const label = body().querySelector('[data-fg-count-label]');
    if (!label) return;
    document.querySelectorAll('[data-fg-cart-count]').forEach((el) => (el.textContent = label.dataset.count));
    document.querySelectorAll('[data-fg-cart-label]').forEach((el) => (el.textContent = label.textContent));
    document.querySelectorAll('[data-fg-cart-link]').forEach((el) => (el.dataset.count = label.dataset.count));
  };

  const bagCount = () => Number(document.querySelector('[data-fg-cart-link]')?.dataset.count || 0);

  // The seal press: the Fine Goods seal lands on the bag icon with the new count in its oval, holds,
  // and fades to leave the count badge. Returns how long the bag should wait before sliding in over it.
  // Reduced motion (or no Web Animations): nothing is shown, the count has already been updated.
  const seal = document.querySelector('[data-fg-press]');
  let sealReady = false;
  const loadSeal = () => {
    if (!seal || reduceMotion.matches || seal.style.getPropertyValue('--fg-mask')) return;
    seal.style.setProperty('--fg-mask', `url("${seal.dataset.mask}")`);
    const image = new Image();
    image.onload = () => { sealReady = true; };
    image.src = seal.dataset.mask;
  };
  // The seal artwork (9 KB) is fetched when the page is idle, so it is there before the first press;
  // if it has not arrived, the press is skipped rather than shown empty.
  if ('requestIdleCallback' in window) requestIdleCallback(loadSeal, { timeout: 4000 });
  else setTimeout(loadSeal, 1500);
  const press = (before, after) => {
    if (!seal || !sealReady || after <= before || reduceMotion.matches || typeof seal.animate !== 'function') return 0;
    seal.querySelector('[data-fg-press-count]').textContent = after;
    seal.getAnimations().forEach((animation) => animation.cancel());
    seal.animate(
      [
        { opacity: 0, transform: 'scale(1.4) rotate(-6deg)', easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        { opacity: 1, transform: 'scale(1) rotate(0deg)', offset: 0.3 },
        { opacity: 1, transform: 'scale(1) rotate(0deg)', offset: 0.76, easing: 'ease-in' },
        { opacity: 0, transform: 'scale(1) rotate(0deg)' },
      ],
      { duration: 2400 }
    );
    return 800;
  };

  const open = () => {
    if (dialog.open) return;
    opener = document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('fg-drawer-open');
    requestAnimationFrame(() => dialog.classList.add('is-open'));
    dialog.querySelector('[data-fg-drawer-close]')?.focus();
  };

  const close = () => {
    if (!dialog.open) return;
    dialog.classList.remove('is-open');
    document.documentElement.classList.remove('fg-drawer-open');
    const done = () => {
      dialog.close();
      opener?.focus?.();
    };
    if (reduceMotion.matches) done();
    else setTimeout(done, 300);
  };

  const status = (text) => {
    const el = dialog.querySelector('[data-fg-drawer-status]');
    if (el) el.textContent = text;
  };

  const post = (url, payload, json) =>
    fetch(url, {
      method: 'POST',
      headers: json
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: json ? JSON.stringify(payload) : payload,
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      return data;
    });

  const refresh = () =>
    fetch(`${window.location.pathname}?sections=${SECTION}`)
      .then((r) => r.json())
      .then((data) => render(data[SECTION]));

  // Add to cart from any product form.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (onCartPage || !(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter?.name === 'checkout') return;
    event.preventDefault();

    const data = new FormData(form);
    if (submitter?.name && submitter.value) data.set(submitter.name, submitter.value);
    data.append('sections', SECTION);
    data.append('sections_url', window.location.pathname);

    loadSeal();
    const formId = form.getAttribute('id'); // form.id would return <input name="id">
    const buttons = [...(formId ? document.querySelectorAll(`[type="submit"][form="${formId}"]`) : []), submitter].filter(Boolean);
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));

    post(routes.cart_add_url || '/cart/add', data, false)
      .then((response) => {
        const before = bagCount();
        render(response.sections?.[SECTION]);
        // A brief "Added ✓" on the button that was pressed and the seal pressed onto the bag icon,
        // then the bag slides in.
        const pressed = buttons.filter((b) => b.textContent.trim());
        const labels = pressed.map((b) => b.textContent);
        pressed.forEach((b) => { b.textContent = dialog.dataset.addedShort || '✓'; b.classList.add('is-added'); });
        setTimeout(() => pressed.forEach((b, i) => { b.textContent = labels[i]; b.classList.remove('is-added'); }), 1600);
        const wait = press(before, bagCount());
        setTimeout(() => { open(); status(dialog.dataset.addedLabel || ''); }, reduceMotion.matches ? 0 : Math.max(450, wait));
      })
      .catch((error) => {
        refresh().finally(() => {
          open();
          status(error?.description || error?.message || dialog.dataset.errorLabel || '');
        });
      })
      .finally(() => buttons.forEach((b) => b.removeAttribute('aria-busy')));
  });

  // Header / compact bar cart links open the drawer.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-fg-cart-link]');
    if (!link || onCartPage || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    status('');
    open();
  });

  // Quantity / remove inside the drawer.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) return close(); // click on the backdrop
    if (event.target.closest('[data-fg-drawer-close]')) return close();

    const button = event.target.closest('[data-fg-line]');
    if (!button) return;
    body().setAttribute('aria-busy', 'true');
    post(
      routes.cart_change_url ? `${routes.cart_change_url}.js` : '/cart/change.js',
      { line: Number(button.dataset.fgLine), quantity: Number(button.dataset.fgQty), sections: [SECTION], sections_url: window.location.pathname },
      true
    )
      .then((response) => render(response.sections?.[SECTION]))
      .catch((error) => refresh().finally(() => status(error?.description || error?.message || '')))
      .finally(() => {
        body().removeAttribute('aria-busy');
        dialog.querySelector('[data-fg-drawer-close]')?.focus();
      });
  });

  // Other features (gift box builder) add to the cart themselves, then ask the bag to refresh and open.
  // detail.press === false: the feature pressed its own seal (the gift box lid), so the bag icon only counts.
  document.addEventListener('fg:cart-refresh', (event) => {
    const before = bagCount();
    refresh().then(() => {
      if (!event.detail?.open) return;
      setTimeout(() => {
        open();
        status(dialog.dataset.addedLabel || '');
      }, event.detail.press === false ? 0 : press(before, bagCount()));
    });
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });
})();
